import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback } from '@theme/utilities';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.sortNumericOptionValues();
    this.addEventListener('change', this.variantChanged.bind(this));
  }

  /**
   * Sorts variant option values in ascending order when all values are numeric (for example, 4mm, 6.5mm, 8mm).
   * Non-numeric option groups (for example, color names) keep their original order.
   */
  sortNumericOptionValues() {
    this.sortFieldsetOptions();
    this.sortSelectOptions();
  }

  /**
   * Sorts radio/button option groups.
   */
  sortFieldsetOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset.variant-option--buttons'));

    for (const fieldset of fieldsets) {
      const labels = Array.from(fieldset.querySelectorAll(':scope > label.variant-option__button-label'));

      if (labels.length < 2) continue;

      const labelsWithNumbers = labels.map((label, index) => {
        const input = label.querySelector('input');
        const valueSource = input?.getAttribute('aria-label') || input?.value || label.textContent || '';
        const numericValue = VariantPicker.parseNumericValue(valueSource);

        return { label, index, numericValue };
      });

      if (labelsWithNumbers.some(({ numericValue }) => numericValue === null)) continue;

      const sortableLabels = labelsWithNumbers.map(({ label, index, numericValue }) => ({
        label,
        index,
        numericValue: Number(numericValue),
      }));

      sortableLabels
        .sort((a, b) => {
          if (a.numericValue === b.numericValue) return a.index - b.index;
          return a.numericValue - b.numericValue;
        })
        .forEach(({ label }) => fieldset.appendChild(label));
    }
  }

  /**
   * Sorts dropdown option groups.
   */
  sortSelectOptions() {
    const selects = Array.from(this.querySelectorAll('select.variant-option__select')).filter(
      (element) => element instanceof HTMLSelectElement
    );

    for (const select of selects) {
      const options = Array.from(select.options);

      if (options.length < 2) continue;

      const optionsWithNumbers = options.map((option, index) => {
        const numericValue = VariantPicker.parseNumericValue(option.textContent || option.value || '');
        return { option, index, numericValue };
      });

      if (optionsWithNumbers.some(({ numericValue }) => numericValue === null)) continue;

      const sortableOptions = optionsWithNumbers.map(({ option, index, numericValue }) => ({
        option,
        index,
        numericValue: Number(numericValue),
      }));

      sortableOptions
        .sort((a, b) => {
          if (a.numericValue === b.numericValue) return a.index - b.index;
          return a.numericValue - b.numericValue;
        })
        .forEach(({ option }) => select.appendChild(option));
    }
  }

  /**
   * Extracts the first numeric value from a variant label.
   * @param {string} value
   * @returns {number | null}
   */
  static parseNumericValue(value) {
    const numericMatch = value.match(/-?\d+(?:\.\d+)?/);
    if (!numericMatch) return null;

    const parsed = Number.parseFloat(numericMatch[0]);
    return Number.isFinite(parsed) ? parsed : null;
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event.target.closest('.size-guide')) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);

    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    const variantId = selectedOption.dataset.variantId || null;
    const isSwatchInput =
      event.target instanceof HTMLInputElement &&
      !!event.target.closest('.variant-option__button-label--has-swatch');

    let requestUrl;

    // For swatch clicks on the main product page, request by variant ID so we can
    // resolve incompatible secondary options (for example unavailable sizes for a color).
    if (isOnProductPage && isSwatchInput && variantId) {
      const requestProductUrl = (newUrl || this.dataset.productUrl || '').split('?')[0];
      requestUrl = `${requestProductUrl}?variant=${variantId}`;
    } else {
      requestUrl = this.buildRequestUrl(selectedOption);
    }

    this.fetchUpdatedSection(requestUrl, loadsNewProduct);

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;

        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch(() => {});
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);
    this.sortNumericOptionValues();

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector(
      'select.variant-option__select option[selected][data-option-value-id], fieldset.variant-option input[data-option-value-id]:checked'
    );

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    return optionValueId || undefined;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(
      this.querySelectorAll(
        'select.variant-option__select option[selected][data-option-value-id], fieldset.variant-option input[data-option-value-id]:checked'
      )
    );

    return selectedOptions
      .map((option) => option.dataset.optionValueId)
      .filter((optionValueId) => !!optionValueId);
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
